/**
 * Add-person control, shared by the tick list (before start, joins the
 * shuffle pool) and the running screen (appends to the end of the queue —
 * BUILD-SPEC.md §7: "pick from the unticked names, or type a new one. Both
 * append to the end.").
 */
import { useId, useState } from "react";
import { Button, Field, Input, makeStyles, Select, tokens } from "@fluentui/react-components";

export interface AddPersonCandidate {
  userId: string;
  displayName: string;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  row: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
});

export interface AddPersonProps {
  /** Roster members not currently ticked/queued — offered as a pick list.
   * Omit or pass an empty array when there's nothing to pick from (e.g. the
   * roster fetch failed and everyone present is already typed in). */
  candidates?: AddPersonCandidate[];
  onAddInvited: (userId: string, displayName: string) => void;
  onAddManual: (displayName: string) => void;
  label?: string;
}

export function AddPerson({ candidates = [], onAddInvited, onAddManual, label = "Add person" }: AddPersonProps) {
  const styles = useStyles();
  const [name, setName] = useState("");
  const [pickedUserId, setPickedUserId] = useState("");
  const selectId = useId();
  const inputId = useId();

  function submitTyped(): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    onAddManual(trimmed);
    setName("");
  }

  function submitPicked(): void {
    const candidate = candidates.find((c) => c.userId === pickedUserId);
    if (!candidate) return;
    onAddInvited(candidate.userId, candidate.displayName);
    setPickedUserId("");
  }

  return (
    <div className={styles.root}>
      {candidates.length > 0 && (
        <Field label={`${label} from this meeting`}>
          <div className={styles.row}>
            <Select
              id={selectId}
              value={pickedUserId}
              onChange={(_, data) => setPickedUserId(data.value)}
            >
              <option value="">Choose a name…</option>
              {candidates.map((c) => (
                <option key={c.userId} value={c.userId}>
                  {c.displayName}
                </option>
              ))}
            </Select>
            <Button appearance="secondary" disabled={!pickedUserId} onClick={submitPicked}>
              Add
            </Button>
          </div>
        </Field>
      )}
      <Field label={`${label} by typing a name`}>
        <div className={styles.row}>
          <Input
            id={inputId}
            value={name}
            placeholder="Type a name"
            onChange={(_, data) => setName(data.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitTyped();
            }}
          />
          <Button appearance="secondary" disabled={!name.trim()} onClick={submitTyped}>
            Add
          </Button>
        </div>
      </Field>
    </div>
  );
}
